import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useAxios from "./useAxios";
import { toaster } from "../utils/toast";
import { debounce } from "../utils/helper";

const sortKeys = (obj) => {
   if (obj === null || typeof obj !== "object") return obj;
   return Object.keys(obj).sort().reduce((acc, k) => { acc[k] = obj[k]; return acc; }, {});
};

const stableStringify = (value) => JSON.stringify(sortKeys(value));

const buildQueryString = (params) => {
   const entries = Object.entries(sortKeys(params)).filter(([, v]) => v !== undefined && v !== null);
   return entries
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(Array.isArray(v) ? v.join(",") : v)}`)
      .join("&");
};

const defaultSelectItems = (data) => data.items;
const defaultSelectTotal = (data) => data.totalCount;

const usePaginatedList = ({
   endpoint,
   defaultLimit = 10,
   searchDebounceMs = 500,
   extraParams = {},
   initialSearch = "",
   initialPage = 1,
   selectItems = defaultSelectItems,
   selectTotal = defaultSelectTotal,
   onSuccess,
   onPageChange,
}) => {
   const [items, setItems] = useState([]);
   const [currentPage, setCurrentPageState] = useState(initialPage);
   const [limit, setLimitState] = useState(defaultLimit);
   const [search, setSearch] = useState(initialSearch);
   const [totalCount, setTotalCount] = useState(0);
   const [isLoading, setIsLoading] = useState(false);
   const [error, setError] = useState(null);

   const { getData } = useAxios();
   const isMountedRef = useRef(true);
   const onSuccessRef = useRef(onSuccess);
   const selectorsRef = useRef({ selectItems, selectTotal });
   const onPageChangeRef = useRef(onPageChange);
   const extraParamsRef = useRef(extraParams);
   const searchRef = useRef(initialSearch);

   useEffect(() => {
      onSuccessRef.current = onSuccess;
      selectorsRef.current = { selectItems, selectTotal };
      onPageChangeRef.current = onPageChange;
      extraParamsRef.current = extraParams;
   });

   useEffect(() => {
      isMountedRef.current = true;
      return () => { isMountedRef.current = false; };
   }, []);

   const extraParamsSignature = stableStringify(extraParams);
   const prevSignatureRef = useRef(extraParamsSignature);

   const refetch = useCallback(async ({ showLoader = true } = {}) => {
      if (!endpoint) return;
      if (showLoader) setIsLoading(true);
      const query = buildQueryString({
         pageNumber: currentPage,
         limit,
         searchText: search,
         ...extraParamsRef.current,
      });
      const resp = await getData(`${endpoint}?${query}`);
      if (!isMountedRef.current) return;
      const { success } = resp || {};
      if (success) {
         const { selectItems: si, selectTotal: st } = selectorsRef.current;
         setItems(si(resp.data) ?? []);
         setTotalCount(st(resp.data) ?? 0);
         setError(null);
         onSuccessRef.current?.(resp.data);
      } else {
         const message = resp?.message || "Failed to load list";
         setError(message);
         toaster({ message, success: false });
      }
      if (showLoader && isMountedRef.current) setIsLoading(false);
   }, [endpoint, currentPage, limit, search, getData]);

   // when external filters change while not on page 1, reset the page and skip this fetch — the page-change render will fetch
   useEffect(() => {
      if (prevSignatureRef.current !== extraParamsSignature && currentPage !== 1) {
         prevSignatureRef.current = extraParamsSignature;
         setCurrentPageState(1);
         return;
      }
      prevSignatureRef.current = extraParamsSignature;
      refetch();
   }, [refetch, extraParamsSignature, currentPage]);

   useEffect(() => {
      onPageChangeRef.current?.(currentPage);
   }, [currentPage]);

   const setCurrentPage = useCallback((next) => {
      setCurrentPageState((prev) => (typeof next === "function" ? next(prev) : next));
   }, []);

   const setLimit = useCallback((next) => {
      setLimitState((prev) => {
         const value = typeof next === "function" ? next(prev) : next;
         return parseInt(value, 10);
      });
      setCurrentPageState(1);
   }, []);

   const setSearchWithReset = useCallback((next) => {
      const value = typeof next === "function" ? next(searchRef.current) : next;
      if (value === searchRef.current) return;
      searchRef.current = value;
      setSearch(value);
      setCurrentPageState(1);
   }, []);

   // stable debounced handler so JSX inputs don't lose focus or rebind on each render
   const onSearchInputChange = useMemo(
      () => debounce((e) => {
         const value = typeof e === "string" ? e : e?.target?.value ?? "";
         setSearchWithReset(value);
      }, searchDebounceMs),
      [searchDebounceMs, setSearchWithReset]
   );

   const totalPages = useMemo(
      () => (limit > 0 ? Math.ceil(totalCount / limit) : 0),
      [totalCount, limit]
   );
      
   useEffect(() => {
   if (totalPages > 0 && currentPage > totalPages) setCurrentPageState(1);
   }, [currentPage, totalPages]);

   return {
      items,
      isLoading,
      error,
      currentPage,
      limit,
      totalCount,
      totalPages,
      search,
      onSearchInputChange,
      setSearch: setSearchWithReset,
      setCurrentPage,
      setLimit,
      setItems,
      refetch,
   };
};

export default usePaginatedList;
